import { computed, signal } from '@preact/signals-core';
import type { GridColumnWidthChange } from '@thinkalpha/ui-table/interactions/columnResize';
import type { GridColumnsInput, GridColumnPin } from '@thinkalpha/ui-table/types/column';
import type { TimeListColumnPreference } from '@thinkalpha/ui-types/contracts/workspace';
import type { StoreAdapter } from 'src/models/StoreAdapter';

export class TimeListColumns implements Disposable {
    readonly #defaults: GridColumnsInput;
    readonly #preferences = signal<readonly TimeListColumnPreference[]>([]);
    readonly #persist: (columns: TimeListColumnPreference[]) => void;
    readonly #unsubscribe: (() => void) | undefined;
    readonly columns;

    constructor(defaults: GridColumnsInput, adapter?: StoreAdapter<{ columns?: TimeListColumnPreference[] }>) {
        this.#defaults = defaults;
        this.#preferences.value = adapter?.getState().columns ?? [];
        this.#persist = (columns) => adapter?.persist({ columns });
        this.#unsubscribe = adapter?.subscribe((state) => {
            this.#preferences.value = state.columns ?? [];
        });
        this.columns = computed(() => ({
            ...this.#defaults,
            columnState: this.#defaults.columnState.map((column) => {
                const saved = this.#preferences.value.find((p) => p.columnId === column.columnId);
                return saved
                    ? {
                          ...column,
                          order: Number.isFinite(saved.order) ? saved.order : column.order,
                          visible: saved.visible,
                          pinned: saved.pinned,
                          width:
                              saved.width !== undefined && Number.isFinite(saved.width) && saved.width >= 40
                                  ? saved.width
                                  : column.width,
                      }
                    : column;
            }),
        }));
    }

    #commit(columns: TimeListColumnPreference[]): void {
        this.#preferences.value = columns;
        this.#persist(columns);
    }
    readonly resize = ({ columnId, width }: GridColumnWidthChange): void => {
        this.#commit(this.columns.peek().columnState.map((c) => (c.columnId === columnId ? { ...c, width } : c)));
    };
    readonly reorder = (ids: readonly string[]): void => {
        this.#commit(
            this.columns.peek().columnState.map((c) => ({
                ...c,
                order: ids.includes(c.columnId) ? ids.indexOf(c.columnId) : c.order,
            })),
        );
    };
    pin(columnId: string, pinned?: GridColumnPin): void {
        this.#commit(this.columns.peek().columnState.map((c) => (c.columnId === columnId ? { ...c, pinned } : c)));
    }
    reset(): void {
        this.#commit([]);
    }
    [Symbol.dispose](): void {
        this.#unsubscribe?.();
    }
}
